import { IApiAxiosError } from "@/api";
import { IAdminScopeJobRun, JobStatus } from "@/api/admin";
import { getScopeJobRun, rerunScope } from "@/api/scopes/scopes";
import { ScopeStatus } from "@/api/scopes/scopes.types";
import { AdminPageContent, AdminPageHeader } from "@/components/admin";
import { Alert } from "@/components/shared/alerts/Alert";
import { MetadataChip } from "@/components/shared/chips/MetadataChip";
import { Modal } from "@/components/shared/Modal";
import { theme } from "@/config/project";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import {
    useMutation,
    useQueryClient,
    useSuspenseQuery,
} from "@tanstack/react-query";
import {
    BookCheck,
    Building2,
    CheckCircle2,
    CircleAlert,
    Clock,
    Loader2,
    RotateCcw,
    User,
} from "lucide-react";
import { useMountEffect } from "@react-hookz/web";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router";
import { JobErrorBanner } from "./components/JobErrorBanner";
import { JobRunSummary } from "./components/JobRunSummary";
import { PhasePanel } from "./components/PhasePanel";
import { PipelineStepper } from "./components/PipelineStepper";
import { groupTasksByPhase, isJobNonTerminal, PHASES } from "./utils";

const STATUS_CHIP_CONFIG: Record<
    string,
    { color: string; bg: string; label: string; icon: React.ElementType }
> = {
    [ScopeStatus.PENDING]: {
        color: "#1e40af",
        bg: "#dbeafe",
        label: "Waiting for Document",
        icon: Clock,
    },
    [ScopeStatus.COMPLETE]: {
        color: "#166534",
        bg: "#dcfce7",
        label: "Completed",
        icon: CheckCircle2,
    },
    [ScopeStatus.PROCESSING]: {
        color: "#92400e",
        bg: "#fef3c7",
        label: "Processing",
        icon: Loader2,
    },
    [ScopeStatus.FAILED]: {
        color: "#AF0101",
        bg: "#fef2f2",
        label: "Failed",
        icon: CircleAlert,
    },
};

const ScopeJobRunPage = () => {
    const { scopeId } = useParams<{ scopeId: string }>();
    const numericScopeId = Number(scopeId);

    if (!scopeId || Number.isNaN(numericScopeId)) {
        return <Navigate to="/admin/scopes" replace />;
    }

    return <ScopeJobRunContent scopeId={numericScopeId} />;
};

const ScopeJobRunContent = ({ scopeId }: { scopeId: number }) => {
    const queryClient = useQueryClient();
    const { setBreadcrumbs } = useBreadcrumb();
    const [rerunModalOpen, setRerunModalOpen] = useState(false);
    const phaseRefs = useRef<(HTMLDivElement | null)[]>([]);

    useDocumentTitle("Scope Job Run");

    const { data } = useSuspenseQuery<IAdminScopeJobRun>({
        queryKey: ["admin-scope-job-run", scopeId],
        queryFn: () => getScopeJobRun(scopeId),
        refetchInterval: (query) => {
            const jobData = query.state.data;
            if (!jobData) return false;
            if (jobData.scope.status === ScopeStatus.PENDING) return 5000;
            if (isJobNonTerminal(jobData.job)) return 5000;
            return false;
        },
    });

    const rerunMutation = useMutation({
        mutationFn: () => rerunScope(scopeId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-scope-job-run", scopeId],
            });
        },
    });

    const rerunMutationErrorMessage = useMemo(() => {
        if (rerunMutation.error) {
            const error = rerunMutation.error as IApiAxiosError;

            return error.response?.data.detail;
        }
    }, [rerunMutation.error]);

    const { scope, job } = data;

    useMountEffect(() => {
        setBreadcrumbs([
            { label: "Admin", path: "home" },
            { label: "Scopes", path: "scopes" },
            { label: scope.document_title },
        ]);
    });

    const groupedTasks = job ? groupTasksByPhase(job.tasks) : null;
    const jobFailed =
        !!job &&
        (job.status === JobStatus.FAILED ||
            job.status === JobStatus.PARTIAL_SUCCESS);

    const chipConfig = STATUS_CHIP_CONFIG[scope.status] ?? {
        color: "#6b7280",
        bg: "#6b72801a",
        label: scope.status,
        icon: CircleAlert,
    };

    const hasNonCompletedPhase =
        groupedTasks &&
        PHASES.some((phase) => {
            const tasks = groupedTasks.get(phase.key) ?? [];
            return (
                tasks.length > 0 && tasks.some((t) => t.status !== "completed")
            );
        });

    const allPhasesCompleted =
        !!job &&
        (job.status === JobStatus.COMPLETED ||
            job.status === JobStatus.PARTIAL_SUCCESS);

    const computeDefaultExpanded = () =>
        PHASES.map((phase, index) => {
            if (!groupedTasks) return false;
            const tasks = groupedTasks.get(phase.key) ?? [];
            if (hasNonCompletedPhase) {
                return tasks.some((t) => t.status !== "completed");
            }
            return allPhasesCompleted && index === 0;
        });

    const [expandedPhases, setExpandedPhases] = useState<boolean[]>(
        computeDefaultExpanded
    );

    const prevJobStatus = useRef(job?.status);
    useEffect(() => {
        if (job?.status !== prevJobStatus.current) {
            prevJobStatus.current = job?.status;
            setExpandedPhases(computeDefaultExpanded());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status]);

    const setPhaseExpanded = (index: number, value: boolean) => {
        setExpandedPhases((prev) =>
            prev.map((v, i) => (i === index ? value : v))
        );
    };

    const handlePhaseClick = (phaseIndex: number) => {
        setPhaseExpanded(phaseIndex, true);
        phaseRefs.current[phaseIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    return (
        <AdminPageContent>
            <AdminPageHeader
                title={scope.document_title}
                subtitle="Pipeline progress and task execution details"
                leftContent={
                    <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        <MetadataChip
                            icon={
                                scope.status === ScopeStatus.PROCESSING ||
                                scope.status === ScopeStatus.PENDING ? (
                                    <CircularProgress
                                        size={16}
                                        sx={{ color: chipConfig.color }}
                                    />
                                ) : (
                                    <chipConfig.icon />
                                )
                            }
                            label={chipConfig.label}
                            backgroundColor={chipConfig.bg}
                            color={chipConfig.color}
                            hoverBackgroundColor={chipConfig.color}
                        />
                        <MetadataChip
                            icon={<User />}
                            label={scope.user.email}
                            backgroundColor={theme.palette.tertiary.main}
                            color={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.main}
                            href={`/admin/users/${scope.user.id}/details`}
                        />
                        <MetadataChip
                            icon={<Building2 />}
                            label={scope.organization.name}
                            backgroundColor="#fef3c7"
                            color="#d97706"
                            hoverBackgroundColor="#d97706"
                            href={`/admin/organizations/${scope.organization.id}/details`}
                        />
                        <MetadataChip
                            icon={<BookCheck />}
                            label={scope.clause_checklist_name}
                            backgroundColor={theme.palette.successLight.main}
                            color={theme.palette.success.main}
                            hoverBackgroundColor={theme.palette.success.main}
                            href={`/admin/checklists/${scope.clause_checklist_id}/details`}
                        />
                    </Stack>
                }
            >
                {scope.status === ScopeStatus.FAILED && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<RotateCcw size={14} />}
                        onClick={() => setRerunModalOpen(true)}
                        sx={{
                            textTransform: "none",
                            backgroundColor: "#233876",
                        }}
                    >
                        Re-run Scope
                    </Button>
                )}
            </AdminPageHeader>

            <div className="mt-6 flex flex-col gap-6">
                {job && <JobErrorBanner job={job} />}

                {job && groupedTasks && (
                    <PipelineStepper
                        phases={PHASES}
                        groupedTasks={groupedTasks}
                        jobFailed={jobFailed}
                        onPhaseClick={handlePhaseClick}
                    />
                )}

                {!job && (
                    <Alert severity="info">
                        {scope.status === ScopeStatus.PENDING
                            ? "Waiting for document to finish processing. The scope will start automatically once the document is ready."
                            : "No job run found for this scope."}
                        {scope.status === ScopeStatus.FAILED && (
                            <> Use the Re-run button to start processing.</>
                        )}
                    </Alert>
                )}

                {job && <JobRunSummary job={job} />}

                {job && groupedTasks && (
                    <Stack spacing={2}>
                        {PHASES.map((phase, index) => {
                            const tasks = groupedTasks.get(phase.key) ?? [];

                            return (
                                <Box
                                    key={phase.key}
                                    ref={(el: HTMLDivElement | null) => {
                                        phaseRefs.current[index] = el;
                                    }}
                                >
                                    <PhasePanel
                                        phase={phase}
                                        phaseIndex={index}
                                        tasks={tasks}
                                        jobFailed={jobFailed}
                                        expanded={
                                            expandedPhases[index] ?? false
                                        }
                                        onToggle={() =>
                                            setPhaseExpanded(
                                                index,
                                                !expandedPhases[index]
                                            )
                                        }
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </div>

            <Modal
                open={rerunModalOpen}
                onClose={() => setRerunModalOpen(false)}
                title="Re-run Scope"
                showCancel
                isLoading={rerunMutation.isPending}
                error={rerunMutationErrorMessage}
                actions={[
                    {
                        label: "Re-run",
                        onClick: () => {
                            rerunMutation.mutate(undefined, {
                                onSuccess: () => setRerunModalOpen(false),
                            });
                        },
                        disabled: rerunMutation.isPending,
                    },
                ]}
            >
                <Typography variant="body2" color="text.secondary">
                    This will restart all processing for this scope. Any
                    existing results will be replaced.
                </Typography>
            </Modal>
        </AdminPageContent>
    );
};

export default ScopeJobRunPage;
